import { MenuItemInfo } from '@tableau/widgets';
import * as React from 'react';
import { defaultMemoize } from 'reselect/lib';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { ButtonProps } from '../../../common/react/components/ButtonControlledComponent';
import { ButtonControlledFlyoutListMenu, ButtonControlledFlyoutListMenuProps } from '../../../common/react/components/ButtonControlledFlyoutListMenu';
import { ExploreContentApi_ContentType } from '../../../ts/ServerApi/types/IContent';
import { ExplorerDropdown } from '../Components/ExplorerDropdown';
import { ExplorerFlyoutPanelProps } from '../Components/ExplorerFlyoutPanel';
import { ExplorerToolbarTestIds } from './ExplorerToolbar';

export type DatasourceViewType = 'datasource' | 'dataconnection';

export interface DatasourceViewTypeInfo {
  displayValue: () => string;
  type: ExploreContentApi_ContentType;
}

const ViewTypeDictionary: { [V in DatasourceViewType]: DatasourceViewTypeInfo} = {
  'datasource' : {
    displayValue: Messages.dataSourceViewTypeDropdown_dataSources,
    type: 'datasource'
  },
  'dataconnection': {
    displayValue: Messages.dataSourceViewTypeDropdown_connections,
    type: 'dataconnection'
  }
};

export type DatasourceViewTypeButtonProps = ButtonProps & DatasourceViewTypeDropdownProps;

export interface DatasourceViewTypeDropdownProps {
  readonly viewType: DatasourceViewType;
  readonly viewTypeSelected: (viewType: DatasourceViewTypeInfo) => void;
  readonly disabled: boolean;
}

export class DatasourceViewTypeDropdown extends React.PureComponent<DatasourceViewTypeDropdownProps> {
  private memoizedPanelContent = defaultMemoize(getPanelContent);

  public constructor(props: DatasourceViewTypeDropdownProps) {
    super(props);
  }

  public render(): JSX.Element {
    return (
      <ExplorerDropdown
        disabled={this.props.disabled}
        testId={DatasourceViewTypeDropdownTestIds.dropDown}
        renderButtonContent={this.renderButtonContent}
        renderPanel={this.renderPanel}
        panelTitle={''}
        buttonStyleOverrides={{height: 'auto'}}
      />
    );
  }

  private handleSelection = (value: string) => {
    this.props.viewTypeSelected(ViewTypeDictionary[value as DatasourceViewType]);
  }

  private renderButtonContent = (_showingOrActive: boolean, _disabled: boolean): JSX.Element => {
    return (
      <div>
        {ViewTypeDictionary[this.props.viewType].displayValue()}
      </div>
    );
  }

  private renderPanel = (props: ExplorerFlyoutPanelProps): JSX.Element => {
    return this.memoizedPanelContent(this.handleSelection, props, this.props);
  }
}

const getPanelContent = (handleSelection: (value: string) => void, flyoutProps: ExplorerFlyoutPanelProps, props: DatasourceViewTypeDropdownProps): JSX.Element => {
  const menuItems = buildMenuItems(props);
  const panelProps: ButtonControlledFlyoutListMenuProps = {
    onComponentDidMount: _.noop,
    menuItems: menuItems,
    onMenuItemSelected: handleSelection,
    anchorElement: flyoutProps.anchorEl,
    closeMe: flyoutProps.onCloseRequest,
    testId: DatasourceViewTypeDropdownTestIds.childPanelWrapper,
    id: `${flyoutProps.testId}-${ExplorerToolbarTestIds.ExplorerDropdownLabelSuffix}`,
    role: 'menu',
  };
  return <ButtonControlledFlyoutListMenu {...panelProps} />;
};

const buildMenuItems = (props: DatasourceViewTypeDropdownProps): MenuItemInfo[] => {
  const items: MenuItemInfo[] = [];

  items.push({
    value: ViewTypeDictionary.datasource.type,
    displayValue: ViewTypeDictionary.datasource.displayValue(),
    disabled: false,
    checked: props.viewType === ViewTypeDictionary.datasource.type
  });

  items.push({
    value: ViewTypeDictionary.dataconnection.type,
    displayValue: ViewTypeDictionary.dataconnection.displayValue(),
    disabled: false,
    checked:  props.viewType === ViewTypeDictionary.dataconnection.type
  });

  return items;
};

export namespace DatasourceViewTypeDropdownTestIds {
  export const dropDown = 'content-explorer-ds-type-dropdown';
  export const button = 'content-explorer-ds-type-button';
  export const childPanelWrapper = 'content-explorer-ds-type-panel-wrapper';
}
